import React, { useEffect, useMemo, useState } from 'react';
import type { ActiveStatus, UnitQuery, UnitResponse } from '@troia-v3/types';

import { Button, DeleteConfirmationModal, FiltersButton, FilterBottomSheet, ListPageEmpty, ListPageLoading, ListPagePagination, PageToolbar, PlainHeadCell, SelectAllHeadCell, SortableHeadCell, ToolbarSearch, ViewToggle } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';
import { useUnits, useDeleteUnit, useUpdateUnit } from '../hooks/useUnits';
import { UnitFormDrawer } from '../components/UnitFormDrawer';
import { UnitCard } from '../components/UnitCard';
import { UnitListItem } from '../components/UnitListItem';

type ViewMode = 'grid' | 'table';
type SortKey = 'name' | 'code' | 'status';

const VIEW_MODE_STORAGE_KEY = 'units_view_mode';

/**
 * UnitsPage — listagem de unidades (padrão-lista travado da casa).
 *
 * Molde estrutural: `src/modules/goals/pages/GoalsPage.tsx` (Drawer de
 * criação/edição em vez de páginas `/new` e `/:id/edit`).
 */
export const UnitsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [query, setQuery] = useState<UnitQuery>({ page: 1, limit: 20, sortBy: 'name', sortOrder: 'asc' });
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; dir: 'asc' | 'desc' }>({ key: 'name', dir: 'asc' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<UnitResponse | null>(null);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    try {
      const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
      return stored === 'grid' || stored === 'table' ? stored : 'table';
    } catch {
      return 'table';
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(VIEW_MODE_STORAGE_KEY, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState<UnitResponse | null>(null);

  const { data, isLoading, error } = useUnits(query);
  const deleteUnit = useDeleteUnit();
  const updateUnit = useUpdateUnit();

  const items = useMemo(() => data?.items ?? [], [data]);

  // A ordenação acontece no SERVIDOR (`sortBy`/`sortOrder` na query) — ordenar
  // client-side reordenava apenas os 20 itens da página sobre um `createdAt
  // desc`, então a página 2 recomeçava o alfabeto.
  const sortedItems = items;

  const selectedCount = selectedIds.size;
  const allOnPageSelected = items.length > 0 && items.every((u) => selectedIds.has(u.id));
  const someOnPageSelected = items.some((u) => selectedIds.has(u.id));

  const setSearch = (term: string) => {
    setSearchTerm(term);
    setQuery((prev) => ({ ...prev, search: term || undefined, page: 1 }));
  };

  const setStatusFilter = (status?: ActiveStatus) => {
    setQuery((prev) => ({ ...prev, filters: status ? { status } : undefined, page: 1 }));
    setFiltersOpen(false);
  };

  const clearAllFilters = () => {
    setQuery((prev) => ({ ...prev, filters: undefined, page: 1 }));
    setFiltersOpen(false);
  };

  const toggleSelect = (id: string) =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleSelectAllOnPage = () =>
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allOnPageSelected) items.forEach((u) => next.delete(u.id));
      else items.forEach((u) => next.add(u.id));
      return next;
    });

  const clearSelection = () => setSelectedIds(new Set());

  const handleSort = (key: SortKey) => {
    const dir: 'asc' | 'desc' = sort.key === key && sort.dir === 'asc' ? 'desc' : 'asc';
    setSort({ key, dir });
    // Volta pra página 1: com ordenação global, manter a página atual mostraria
    // uma fatia do meio da nova ordem.
    setQuery((prev) => ({ ...prev, sortBy: key, sortOrder: dir, page: 1 }));
  };

  const handleNew = () => {
    setEditing(null);
    setDrawerOpen(true);
  };

  // "+" central do bottom-nav mobile → nova unidade (mockup .m-fab).
  useEffect(() => {
    const onFab = (e: Event) => { e.preventDefault(); handleNew(); };
    window.addEventListener('troia:mobile-fab', onFab);
    return () => window.removeEventListener('troia:mobile-fab', onFab);
  }, []);

  const handleEdit = (unit: UnitResponse) => {
    setEditing(unit);
    setDrawerOpen(true);
  };

  const handleDelete = (unit: UnitResponse) => setDeleteTarget(unit);

  const confirmDelete = () => {
    if (deleteTarget) deleteUnit.mutate(deleteTarget.id);
    setDeleteTarget(null);
  };

  const handleToggleStatus = (unit: UnitResponse) => {
    const nextStatus: ActiveStatus = unit.status === 'active' ? 'inactive' : 'active';
    updateUnit.mutate({ id: unit.id, data: { status: nextStatus } });
  };

  const bulkDeactivate = () => {
    // Percorre TODA a seleção, não só a página carregada: a seleção sobrevive à
    // paginação e o contador da barra soma tudo, então filtrar por `items`
    // desativava um subconjunto silencioso do que o usuário mandou desativar.
    // (`bulkDelete` já usava `selectedIds` — eram os dois inconsistentes.)
    // Desativar quem já está inativo é no-op no backend, então não filtramos
    // por status: para os itens fora da página nem sabemos qual é.
    Array.from(selectedIds).forEach((id) => updateUnit.mutate({ id, data: { status: 'inactive' } }));
    clearSelection();
  };

  const bulkDelete = () => setBulkDeleteOpen(true);

  const confirmBulkDelete = () => {
    Array.from(selectedIds).forEach((id) => deleteUnit.mutate(id));
    clearSelection();
    setBulkDeleteOpen(false);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditing(null);
  };

  const statusLabel = query.filters?.status === 'active' ? 'Ativas' : query.filters?.status === 'inactive' ? 'Inativas' : null;
  const activeFilterCount = [statusLabel].filter(Boolean).length;

  // Conteúdo dos filtros (compartilhado: popover desktop + bottom-sheet mobile)
  const filterOptions = (
    <>
      <p className="px-2.5 pt-1.5 pb-1 text-[11px] font-bold uppercase tracking-wider" style={{ color: '#94a3b8' }}>Status</p>
      {([[undefined, 'Todas'], ['active', 'Ativas'], ['inactive', 'Inativas']] as const).map(([val, label]) => {
        const active = (query.filters?.status ?? undefined) === val;
        return (
          <button
            key={label}
            onClick={() => setStatusFilter(val)}
            className="w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-sm font-medium text-left"
            style={{ color: '#374151', backgroundColor: active ? '#f3e8ff' : 'transparent' }}
            onMouseEnter={(e) => { if (!active) e.currentTarget.style.backgroundColor = '#f1f5f9'; }}
            onMouseLeave={(e) => { if (!active) e.currentTarget.style.backgroundColor = 'transparent'; }}
            data-testid={`units-filter-status-${label}`}
          >
            {label}
            {active && <Icon name="check" className="w-4 h-4" style={{ color: '#8b5cf6' }} />}
          </button>
        );
      })}
    </>
  );

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: 'var(--theme-page-background)' }}>
      {/* ── Toolbar 3 zonas (contrato DS 3.3) ── */}
      <PageToolbar
        left={<>
          {/* Esquerda: título + contador (módulo top-level, sem breadcrumb — molde WebsitesListPage) */}
          <span className="font-bold text-xl tracking-tight truncate" style={{ color: 'var(--theme-text)' }}>Unidades</span>
          {data?.total != null && (
            <span
              className="text-xs font-semibold rounded-full px-2 py-0.5 tabular-nums flex-none"
              style={{ backgroundColor: 'var(--theme-surface-sunken)', color: 'var(--theme-muted-text)' }}
              data-testid="units-count"
            >
              {data.total}
            </span>
          )}
        </>}
        center={
          /* Centro: busca (F17-22: ToolbarSearch shared, sempre centralizada) */
          <ToolbarSearch
            value={searchTerm}
            onChange={setSearch}
            placeholder="Buscar unidade…"
            data-testid="units-search"
          />
        }
        right={<>
          {/* Direita — ordem canônica (F15): Filtros → Toggle → sep → CTA */}
          <div className="relative flex-none">
            <FiltersButton
              count={activeFilterCount}
              active={filtersOpen}
              onClick={() => setFiltersOpen((v) => !v)}
              data-testid="units-filters"
            />
            {filtersOpen && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setFiltersOpen(false)} />
                <div
                  className="absolute right-0 top-full mt-2 z-50 w-60 rounded-xl p-2 max-h-[70vh] overflow-y-auto"
                  style={{ backgroundColor: '#ffffff', border: '1px solid #e5e7eb', boxShadow: '0 4px 24px rgba(0,0,0,.12)' }}
                  data-testid="units-filters-popover"
                >
                  {filterOptions}
                </div>
              </>
            )}
          </div>

          {/* View toggle (F09-11: ViewToggle shared — Lista primeiro, rótulo Cards) */}
          <ViewToggle
            modes={[
              { id: 'table', icon: 'list', label: 'Lista' },
              { id: 'grid', icon: 'grid', label: 'Cards' },
            ]}
            value={viewMode}
            onChange={setViewMode}
            data-testid="units-view"
          />

          {/* .tb-sep antes do CTA */}
          <span className="hidden md:block w-px h-[22px] flex-none" style={{ backgroundColor: 'var(--theme-input-border)' }} />

          {/* Nova unidade (único primário — F01-03: Button shared com colapso) */}
          <Button
            variant="primary"
            icon="plus"
            collapsibleLabel
            onClick={handleNew}
            data-testid="new-unit"
          >
            Nova unidade
          </Button>
        </>}
        mobile={
          /* `.m-sub` do mockup: busca sunken + Filtros (abre bottom-sheet) */
          <>
            <ToolbarSearch
              value={searchTerm}
              onChange={setSearch}
              placeholder="Buscar unidade…"
              data-testid="units-search-mobile"
            />
            <button
              onClick={() => setFiltersOpen((v) => !v)}
              className="inline-flex items-center gap-1.5 h-[38px] px-3 rounded-[10px] text-[13px] font-semibold whitespace-nowrap flex-none"
              style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }}
              data-testid="units-filters-mobile"
            >
              <Icon name="filter" className="w-4 h-4" style={{ color: 'var(--theme-muted-text)' }} />
              Filtros
              {activeFilterCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold text-white" style={{ backgroundColor: 'var(--theme-primary)' }}>{activeFilterCount}</span>
              )}
            </button>
          </>
        }
      />

      {/* ── Filterbar (chips) — some quando vazia ── */}
      {activeFilterCount > 0 && (
        <div className="flex-none flex items-center flex-wrap gap-2 px-4 py-2" style={{ backgroundColor: 'var(--theme-header-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          {statusLabel && (
            <span className="inline-flex items-center gap-1 h-[26px] pl-2.5 pr-1 rounded-full text-xs font-semibold" style={{ backgroundColor: 'var(--theme-primary-background)', color: 'var(--theme-primary)' }}>
              Status: {statusLabel}
              <button onClick={() => setStatusFilter(undefined)} className="w-[18px] h-[18px] rounded-full inline-flex items-center justify-center opacity-60 hover:opacity-100" aria-label="Remover filtro de status">
                <Icon name="x" className="w-3 h-3" />
              </button>
            </span>
          )}
          <button onClick={clearAllFilters} className="text-xs font-semibold" style={{ color: 'var(--theme-muted-text)' }} data-testid="units-clear-filters">Limpar tudo</button>
        </div>
      )}

      {/* ── Bulk bar ── */}
      {selectedCount > 0 && (
        <div className="flex-none flex items-center gap-2.5 px-4 py-2" style={{ backgroundColor: 'var(--theme-primary-background)', borderBottom: '1px solid var(--theme-header-border)' }}>
          <span className="font-bold text-sm" style={{ color: 'var(--theme-primary)' }}>{selectedCount} selecionada(s)</span>
          <span className="flex-1" />
          <button onClick={bulkDeactivate} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ border: '1px solid var(--theme-input-border)', backgroundColor: 'var(--theme-card-background)', color: 'var(--theme-card-text)' }} data-testid="units-bulk-deactivate">
            <Icon name="power" className="w-3.5 h-3.5" /> Desativar
          </button>
          <button onClick={bulkDelete} className="inline-flex items-center gap-1.5 h-[30px] px-2.5 rounded-lg text-sm font-semibold" style={{ backgroundColor: 'var(--theme-error-subtle)', color: 'var(--theme-error-fg)' }} data-testid="units-bulk-delete">
            <Icon name="trash" className="w-3.5 h-3.5" /> Excluir
          </button>
          <button onClick={clearSelection} className="text-sm font-semibold" style={{ color: 'var(--theme-primary)' }} data-testid="units-bulk-clear">Cancelar</button>
        </div>
      )}

      {/* ── Conteúdo ── */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 md:px-8 py-6">
          {error && (
            <div className="rounded-lg p-4" style={{ backgroundColor: 'var(--theme-error-subtle)', border: '1px solid var(--theme-error-color)' }} data-testid="units-error">
              <p style={{ color: 'var(--theme-error-fg)' }}>Erro ao carregar unidades: {error.message}</p>
            </div>
          )}

          {isLoading && <ListPageLoading message="Carregando unidades..." />}

          {!isLoading && !error && items.length === 0 && (
            <ListPageEmpty
              icon="map-pin"
              message="Nenhuma unidade encontrada"
              description={searchTerm || activeFilterCount > 0 ? 'Tente ajustar a busca ou os filtros.' : 'Crie sua primeira unidade para organizar equipes e checklists por local.'}
              actionLabel="Nova unidade"
              onAction={handleNew}
            />
          )}

          {/* Grid (Cards) — F34: cards SEM checkbox, seleção em massa só na tabela */}
          {!isLoading && !error && items.length > 0 && viewMode === 'grid' && (
            <div className="grid [grid-template-columns:repeat(auto-fill,minmax(280px,1fr))] gap-4" data-testid="units-grid">
              {sortedItems.map((unit) => (
                <UnitCard
                  key={unit.id}
                  unit={unit}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  onToggleStatus={handleToggleStatus}
                />
              ))}
            </div>
          )}

          {/* Tabela (Lista) — vira cards no mobile */}
          {!isLoading && !error && items.length > 0 && viewMode === 'table' && (
            <>
              {/* Mobile: cards */}
              <div className="grid grid-cols-1 gap-4 md:hidden">
                {sortedItems.map((unit) => (
                  <UnitCard
                    key={unit.id}
                    unit={unit}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onToggleStatus={handleToggleStatus}
                  />
                ))}
              </div>
              {/* Desktop: tabela */}
              <div className="hidden md:block rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--theme-card-background)', border: '1px solid var(--theme-card-border)' }}>
                <table className="w-full border-collapse" data-testid="units-table">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--theme-card-border)' }}>
                      <SelectAllHeadCell
                        checked={allOnPageSelected}
                        indeterminate={someOnPageSelected && !allOnPageSelected}
                        onToggle={toggleSelectAllOnPage}
                        ariaLabel="Selecionar todas"
                        data-testid="units-select-all"
                      />
                      <SortableHeadCell<SortKey> label="Unidade" sortKey="name" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <SortableHeadCell<SortKey> label="Código" sortKey="code" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell label="Endereço" />
                      <SortableHeadCell<SortKey> label="Status" sortKey="status" activeKey={sort.key} dir={sort.dir} onSort={handleSort} />
                      <PlainHeadCell className="w-14" />
                    </tr>
                  </thead>
                  <tbody>
                    {sortedItems.map((unit) => (
                      <UnitListItem
                        key={unit.id}
                        unit={unit}
                        selected={selectedIds.has(unit.id)}
                        onToggleSelect={toggleSelect}
                        onEdit={handleEdit}
                        onDelete={handleDelete}
                        onToggleStatus={handleToggleStatus}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Paginação */}
          {!isLoading && !error && data?.page != null && data?.totalPages != null && (
            <ListPagePagination
              currentPage={data.page}
              totalPages={data.totalPages}
              totalItems={data.total}
              pageSize={data.limit}
              itemLabel="unidades"
              onPageChange={(page) => setQuery((prev) => ({ ...prev, page }))}
            />
          )}
        </div>
      </div>

      {/* Bottom-sheet de filtros (mobile) — reusa o mesmo conteúdo do popover */}
      <FilterBottomSheet isOpen={filtersOpen} onClose={() => setFiltersOpen(false)} data-testid="units-filters-sheet">
        {filterOptions}
      </FilterBottomSheet>

      <UnitFormDrawer
        isOpen={drawerOpen}
        onClose={handleCloseDrawer}
        unit={editing}
      />

      {/* Confirmação de exclusão (single) */}
      <DeleteConfirmationModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        message="Tem certeza que deseja excluir esta unidade?"
        isLoading={deleteUnit.isPending}
      />

      {/* Confirmação de exclusão (bulk) */}
      <DeleteConfirmationModal
        isOpen={bulkDeleteOpen}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={confirmBulkDelete}
        message={`Excluir ${selectedCount} unidade(s) selecionada(s)?`}
        isLoading={deleteUnit.isPending}
      />
    </div>
  );
};

UnitsPage.displayName = 'UnitsPage';
